import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { destinoDepoisDoLogin } from "@/pages/LoginPage";

function Moldura({ titulo, children }) {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-3xl font-bold text-teal-700 dark:text-teal-400" style={{ fontFamily: "Manrope" }}>Aesthetica</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pricing Pro</p>
          </Link>
        </div>
        <Card className="border-slate-200 dark:border-slate-800 shadow-lg">
          {titulo && (
            <CardHeader className="pb-2">
              <CardTitle className="text-xl text-center text-slate-900 dark:text-white">{titulo}</CardTitle>
            </CardHeader>
          )}
          <CardContent className={titulo ? "" : "pt-8 pb-8"}>{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}

export function RedefinirSenhaPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { formatApiError } = useAuth();
  const token = params.get("token") || "";
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    if (senha.length < 8) return toast.error("A senha precisa ter pelo menos 8 caracteres");
    if (senha !== confirmacao) return toast.error("As senhas não coincidem");
    setSalvando(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: senha });
      setPronto(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSalvando(false);
    }
  };

  if (!token) {
    return (
      <Moldura>
        <div className="space-y-4 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
          <p className="text-slate-600 dark:text-slate-300">Link incompleto. Abra o link exatamente como chegou no e-mail.</p>
          <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>Ir para o login</Button>
        </div>
      </Moldura>
    );
  }

  if (pronto) {
    return (
      <Moldura>
        <div className="space-y-4 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
          <p className="text-slate-700 dark:text-slate-200 font-medium">Senha alterada!</p>
          <p className="text-sm text-slate-500">Por segurança, os outros aparelhos foram desconectados.</p>
          <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={() => navigate("/login")}>Entrar com a senha nova</Button>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura titulo="Criar uma senha nova">
      <form onSubmit={enviar} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="nova">Senha nova</Label>
          <Input id="nova" type="password" value={senha} onChange={(e) => setSenha(e.target.value)}
            placeholder="Mínimo 8 caracteres" autoComplete="new-password" required data-testid="reset-new-password-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="conf">Repita a senha</Label>
          <Input id="conf" type="password" value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)}
            autoComplete="new-password" required data-testid="reset-confirm-password-input" />
        </div>
        <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={salvando} data-testid="reset-submit-btn">
          {salvando ? "Salvando..." : "Salvar senha nova"}
        </Button>
      </form>
    </Moldura>
  );
}

// Link do convite de administrador: cria a senha e ja entra no sistema.
export function ConvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { formatApiError, setUserDirect } = useAuth();
  const token = params.get("token") || "";
  const [estado, setEstado] = useState("verificando"); // verificando | pronto | erro
  const [convidado, setConvidado] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const iniciado = useRef(false);

  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;
    if (!token) {
      setEstado("erro");
      setMensagem("Link incompleto. Abra o link exatamente como chegou no e-mail.");
      return;
    }
    axios
      .post(`${API}/auth/convite/verificar`, { token })
      .then((r) => {
        setConvidado(r.data);
        setEstado("pronto");
      })
      .catch((err) => {
        setEstado("erro");
        setMensagem(formatApiError(err.response?.data?.detail));
      });
  }, [token, formatApiError]);

  const enviar = async (e) => {
    e.preventDefault();
    if (senha.length < 8) return toast.error("A senha precisa ter pelo menos 8 caracteres");
    if (senha !== confirmacao) return toast.error("As senhas não coincidem");
    setSalvando(true);
    try {
      const r = await axios.post(`${API}/auth/aceitar-convite`, { token, new_password: senha });
      if (r.data.requer_login) {
        toast.success(r.data.message);
        navigate("/login", { replace: true });
        return;
      }
      setUserDirect(r.data);
      toast.success("Senha criada. Bem-vindo(a)!");
      navigate(destinoDepoisDoLogin(r.data), { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSalvando(false);
    }
  };

  if (estado === "verificando") {
    return (
      <Moldura>
        <div className="space-y-4 text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-teal-600" />
          <p className="text-slate-600 dark:text-slate-300">Abrindo o seu convite...</p>
        </div>
      </Moldura>
    );
  }

  if (estado === "erro") {
    return (
      <Moldura>
        <div className="space-y-4 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
          <p className="text-slate-600 dark:text-slate-300">{mensagem}</p>
          <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>Ir para o login</Button>
        </div>
      </Moldura>
    );
  }

  return (
    <Moldura titulo="Crie a sua senha">
      <form onSubmit={enviar} className="space-y-4">
        <p className="text-center text-sm text-slate-500">
          Acesso de administrador para <b className="text-slate-700 dark:text-slate-200">{convidado?.email}</b>
        </p>
        <div className="space-y-2">
          <Label htmlFor="senha-convite">Senha</Label>
          <Input id="senha-convite" type="password" value={senha} onChange={(e) => setSenha(e.target.value)}
            placeholder="Mínimo 8 caracteres" autoComplete="new-password" required data-testid="convite-senha" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="conf-convite">Repita a senha</Label>
          <Input id="conf-convite" type="password" value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)}
            autoComplete="new-password" required data-testid="convite-confirmacao" />
        </div>
        <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={salvando} data-testid="convite-salvar">
          {salvando ? "Salvando..." : "Criar senha e entrar"}
        </Button>
      </form>
    </Moldura>
  );
}

export function ConfirmarEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [estado, setEstado] = useState("confirmando"); // confirmando | ok | erro
  const [mensagem, setMensagem] = useState("");
  const iniciado = useRef(false);

  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;
    const token = params.get("token");
    if (!token) {
      setEstado("erro");
      setMensagem("Link incompleto. Abra o link exatamente como chegou no e-mail.");
      return;
    }
    axios
      .post(`${API}/auth/verificar-email`, { token })
      .then(() => {
        setEstado("ok");
        refreshUser();
      })
      .catch((err) => {
        setEstado("erro");
        setMensagem(err.response?.data?.detail || "Não foi possível confirmar o e-mail.");
      });
  }, [params, refreshUser]);

  return (
    <Moldura>
      <div className="space-y-4 text-center">
        {estado === "confirmando" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-teal-600" />
            <p className="text-slate-600 dark:text-slate-300">Confirmando seu e-mail...</p>
          </>
        )}
        {estado === "ok" && (
          <>
            <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
            <p className="font-medium text-slate-700 dark:text-slate-200">E-mail confirmado. Obrigado!</p>
            <Button className="w-full bg-teal-600 hover:bg-teal-700" onClick={() => navigate(user ? "/" : "/login")}>
              {user ? "Ir para o app" : "Entrar"}
            </Button>
          </>
        )}
        {estado === "erro" && (
          <>
            <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
            <p className="text-slate-600 dark:text-slate-300">{mensagem}</p>
            <Button variant="outline" className="w-full" onClick={() => navigate(user ? "/conta" : "/login")}>
              {user ? "Pedir um link novo em Minha conta" : "Ir para o login"}
            </Button>
          </>
        )}
      </div>
    </Moldura>
  );
}
